// -- Type definitions
class MomaxHomeCategories extends HTMLElement {
  // -- Lifecycle hooks
  connectedCallback() {
    this.track = this.querySelector('[data-hc-track]');
    if (!this.track) return;
    this.cards = [...this.querySelectorAll('[data-hc-card]')];
    this.pages = [...this.querySelectorAll('[data-hc-page]')];
    this.previous = this.querySelector('[data-hc-direction="-1"]');
    this.next = this.querySelector('[data-hc-direction="1"]');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('click', event => {
      const arrow = event.target.closest('[data-hc-direction]');
      const page = event.target.closest('[data-hc-page]');
      if (arrow) this.step(Number(arrow.dataset.hcDirection));
      if (page) this.goTo(this.position(this.cards[Number(page.dataset.hcPage)]));
    }, options);
    this.track.addEventListener('scroll', () => this.update(), { ...options, passive: true });
    this.track.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.goTo(0);
      else if (event.key === 'End') this.goTo(this.maximum());
      else this.step(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    this.addEventListener('shopify:block:select', event => {
      const card = event.target.closest('[data-hc-card]');
      if (card) this.goTo(this.position(card), 'auto');
    }, options);

    let isDown = false;
    let startX = 0;
    let scrollLeftStart = 0;
    let hasDragged = false;

    this.track.addEventListener('mousedown', event => {
      if (event.button !== 0 || window.matchMedia('(max-width: 740px)').matches) return;
      isDown = true;
      hasDragged = false;
      startX = event.pageX;
      scrollLeftStart = this.track.scrollLeft;
      this.track.classList.add('is-pointer-down');
    }, options);

    window.addEventListener('mousemove', event => {
      if (!isDown) return;
      const dx = event.pageX - startX;
      if (Math.abs(dx) > 4) {
        if (!hasDragged) {
          hasDragged = true;
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = scrollLeftStart - dx;
      }
    }, options);

    const onMouseUp = () => {
      if (!isDown) return;
      isDown = false;
      this.track.classList.remove('is-pointer-down');
      if (hasDragged) {
        this.track.classList.remove('is-dragging');
      }
    };

    window.addEventListener('mouseup', onMouseUp, options);

    this.track.addEventListener('click', event => {
      if (hasDragged) {
        event.preventDefault();
        event.stopPropagation();
        hasDragged = false;
      }
    }, { capture: true, ...options });

    this.shadowCarousel = this.querySelector('.momax-home-categories__carousel');
      this.track.addEventListener('pointerover', () => this.updateHoverShadow(), { signal: this.controller.signal });
      this.track.addEventListener('pointerout', () => this.clearHoverShadow(), { signal: this.controller.signal });
      this.track.addEventListener('pointerleave', () => this.clearHoverShadow(), { signal: this.controller.signal });
      this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.track);
    this.cards.forEach(card => this.observer.observe(card));
    this.update();
  }

  disconnectedCallback() {
    this.clearHoverShadow();
      this.controller?.abort();
    this.observer?.disconnect();
  }

  // -- Functions
  maximum() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }

  position(card) {
    if (!card) return 0;
    // Every mobile page starts at its first card, including editor selections in later rows.
    if (matchMedia('(max-width: 740px)').matches) {
      card = this.cards[Math.floor(this.cards.indexOf(card) / 6) * 6];
    }
    const inset = parseFloat(getComputedStyle(this.track).paddingLeft);
    return Math.min(this.maximum(), Math.max(0, card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft - inset));
  }

  goTo(left, behavior) {
    this.track.scrollTo({ left, behavior: behavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth') });
  }

  step(direction) {
    const stops = [...new Set([0, ...this.cards.map(card => this.position(card)), this.maximum()])];
    const current = this.track.scrollLeft;
    const target = direction > 0 ? stops.find(stop => stop > current + 2) : stops.reverse().find(stop => stop < current - 2);
    if (target !== undefined) this.goTo(target);
  }

  clearHoverShadow() {
      this.shadowCarousel?.classList.remove('has-hover-shadow');
      this.querySelector('.has-external-shadow')?.classList.remove('has-external-shadow');
    }

    updateHoverShadow() {
      this.clearHoverShadow();
      if (!this.shadowCarousel || !matchMedia('(min-width: 741px) and (hover: hover) and (pointer: fine)').matches) return;
      const card = this.querySelector('.momax-home-categories__card:hover');
      if (!card || !card.getClientRects().length) return;
      const bounds = card.getBoundingClientRect();
      const viewport = this.track.getBoundingClientRect();
      // Partially visible cards keep their normal clipped shadow, never a phantom outer edge.
      if (bounds.left < viewport.left - 0.5 || bounds.right > viewport.right + 0.5) return;
      const carousel = this.shadowCarousel.getBoundingClientRect();
      for (const [property, value] of Object.entries({left: bounds.left - carousel.left, top: bounds.top - carousel.top, width: bounds.width, height: bounds.height})) {
        this.shadowCarousel.style.setProperty('--hover-' + property, value + 'px');
      }
      card.classList.add('has-external-shadow');
      this.shadowCarousel.classList.add('has-hover-shadow');
    }

    update() {
      this.updateHoverShadow();
    const max = this.maximum();
    const current = this.track.scrollLeft;
    this.querySelector('[data-hc-arrows]').hidden = max < 2;
    this.querySelector('[data-hc-progress]').hidden = max < 2;
    this.previous.disabled = current <= 2;
    this.next.disabled = current >= max - 2;
    // Mobile pagination follows six-card pages, including a partially filled last page.
    let active = 0;
    this.pages.forEach((page, index) => {
      if (current + 2 >= this.position(this.cards[Number(page.dataset.hcPage)])) active = index;
    });
    this.pages.forEach((page, index) => {
      if (index === active) page.setAttribute('aria-current', 'true');
      else page.removeAttribute('aria-current');
    });
  }
}

// -- Lifecycle hooks
if (!customElements.get('momax-home-categories')) customElements.define('momax-home-categories', MomaxHomeCategories);
